import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { EntryStatusEnum } from '@clubspark-node/clubspark-tennis-types';
import { DialogActions, DialogContent, Grid, Link, MenuItem } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { entryStatusConstants } from 'tods-competition-factory';

import TMXDialogAccordion from 'components/accordion/dialogAccordion/TMXDialogAccordion';
import StandardButton from 'components/buttons/StandardButton';
import { useStyles } from 'components/dialogs/addToTeamDialog/styles';
import { useStyles as dialogStyles } from 'components/dialogs/styles';
import TMXDialog from 'components/dialogs/TMXDialog';
import FormErrorMessage from 'components/forms/FormErrorMessage';
import RadioButtonGroup from 'components/radioButtonGroup/RadioButtonGroup';
import TMXSelect from 'components/selectors/tmxSelector/TMXSelect';
import { BodyLarge, BodyRegular, BodyRegularBold, FormLabelTextLarge, H3 } from 'components/typography/Typography';
import { TEAMS_PART } from 'config/router/constants';
import { getParticipantRoutes } from 'config/router/utils';
import { usePromoteToMainDrawDialog } from 'containers/dialogs/PromoteToMainDraw';
import { useProgress } from 'hooks/tournament/useSaveTournament';
import useTournamentParticipants from 'hooks/tournament/useTournamentParticipants';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';
import { getMainDrawEvents, getTeamsFromParticipants } from 'utils/participant';

import { getTeamType } from './utils';

export interface AddToTeamProps {
  eventId?: string;
  loading: boolean;
  open?: boolean;
  selectedPlayers: ParticipantInterface[];
  showAllTeams: boolean;
  onAddToTeam: (playerIds: string[], teamId: string) => Promise<void>;
  onClose?: () => void;
  onExited?: () => void;
  onResetRowSelection?: () => void;
}

const AddToTeamDialog: React.FC<React.PropsWithChildren<AddToTeamProps>> = ({
  eventId,
  loading,
  open,
  selectedPlayers,
  showAllTeams,
  onAddToTeam,
  onClose,
  onExited,
  onResetRowSelection,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { classes: dialogClasses } = dialogStyles();
  const addToTeamInProgress = useProgress('addToTeam');
  const openPromoteToMainDrawDialog = usePromoteToMainDrawDialog();
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const paperProps = useMemo(() => ({ className: dialogClasses.paper }), [dialogClasses.paper]);

  const playerIds = useMemo(() => selectedPlayers.map((player) => player?.participantId), [selectedPlayers]);
  const { tournamentParticipants } = useTournamentParticipants();

  const teams = useMemo(() => getTeamsFromParticipants(tournamentParticipants), [tournamentParticipants]);
  const availableTeams = useMemo(
    () => (eventId ? teams?.filter((team) => team?.events?.map((event) => event.eventId)?.includes(eventId)) : teams),
    [teams, eventId],
  );

  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [choosenTeamType, setChoosenTeamType] = useState<EntryStatusEnum | ''>('');
  const [teamsToShow, setTeamsToShow] = useState([]);
  const [showChooseTeamError, setShowChooseTeamError] = useState<boolean>(false);
  const teamData = useMemo(() => teams?.find((t) => t?.participantId === selectedTeam), [selectedTeam, teams]);
  const mainDrawEvents = useMemo(() => getMainDrawEvents(teamData), [teamData]);

  const selectedTeamMainDraws = useMemo(
    () => getMainDrawEvents(teamData)?.flatMap((event) => event.eventId) ?? [],
    [teamData],
  );
  const handleOnChangeTeamType = useCallback((event) => {
    setChoosenTeamType(event.target.value);
    setSelectedTeam('');
  }, []);

  const handleChangeTeam = useCallback((event) => {
    setShowChooseTeamError(false);
    setSelectedTeam(event.target.value);
  }, []);

  const handleAddToTeam = useCallback(
    async (playerIds: string[], teamId: string) => {
      await onAddToTeam(playerIds, teamId);
      onClose?.();
      onResetRowSelection?.();
    },
    [onAddToTeam, onClose, onResetRowSelection],
  );

  const handleConfirmAddToTeam = useCallback(async () => {
    if (selectedTeam === '') {
      setShowChooseTeamError(true);
      return;
    }
    if (mainDrawEvents?.length > 0) {
      openPromoteToMainDrawDialog({
        addToTeamParticipants: selectedPlayers,
        eventsIds: selectedTeamMainDraws,
        loading: loading || addToTeamInProgress,
        promoteParticipants: [teamData],
        tournamentId: tournamentId,
        onAddToTeam: handleAddToTeam,
      });
    } else {
      await onAddToTeam(playerIds, selectedTeam);
      onClose?.();
      onResetRowSelection?.();
    }
  }, [
    addToTeamInProgress,
    handleAddToTeam,
    loading,
    mainDrawEvents?.length,
    onAddToTeam,
    onClose,
    onResetRowSelection,
    openPromoteToMainDrawDialog,
    playerIds,
    selectedPlayers,
    selectedTeam,
    selectedTeamMainDraws,
    teamData,
    tournamentId,
  ]);

  const findChoosenTeamType = useCallback(
    (teamType: EntryStatusEnum | '') => {
      return availableTeams.filter((team) => {
        return team?.events.some((teamEvent: any) => {
          return teamEvent.eventId === eventId && teamEvent.entryStatus === teamType;
        });
      });
    },
    [eventId, availableTeams],
  );

  const selectedTeams = useMemo(() => {
    return availableTeams.filter((team) =>
      team?.events.some(
        (teamEvent: any) =>
          teamEvent.eventId === eventId && entryStatusConstants.DIRECT_ENTRY_STATUSES.includes(teamEvent.entryStatus),
      ),
    );
  }, [availableTeams, eventId]);

  const alternateTeams = useMemo(() => {
    return availableTeams.filter((team) =>
      team?.events.some(
        (teamEvent: any) => teamEvent.eventId === eventId && teamEvent.entryStatus === EntryStatusEnum.ALTERNATE,
      ),
    );
  }, [availableTeams, eventId]);

  const isThereFilteredTeams = !!selectedTeams.length || !!alternateTeams.length;
  const isThereTeamsToShow = !!(isThereFilteredTeams || showAllTeams) && !!teams.length;

  const handleOnEnter = useCallback(() => {
    setChoosenTeamType(getTeamType(selectedTeams?.length, alternateTeams?.length));
  }, [selectedTeams.length, alternateTeams.length]);

  useEffect(() => {
    const filteredTeams = findChoosenTeamType(choosenTeamType);
    setTeamsToShow(filteredTeams);
  }, [choosenTeamType, findChoosenTeamType]);

  return (
    <TMXDialog
      fullWidth
      open={open || addToTeamInProgress}
      PaperProps={paperProps}
      TransitionProps={{ onEnter: handleOnEnter, onExited }}
    >
      <H3>{showAllTeams ? t('addToTeam') : t('moveToTeam')}</H3>
      <DialogContent className={classes.dialogContent}>
        <Grid container>
          {showAllTeams && (
            <Grid item xs={12}>
              <FormLabelTextLarge>{t('teams.playersUpper')}</FormLabelTextLarge>
              <TMXDialogAccordion
                accordionContent={
                  <Grid container direction="column">
                    {selectedPlayers?.map((item, i) => {
                      const failed = false;
                      const className = `${classes.listItem} ${failed && classes.failed}`;
                      return (
                        <Grid key={item?.participantId} container justifyContent="space-between">
                          <Grid item className={className}>
                            <BodyRegular>{`${i + 1}. ${item?.person?.standardGivenName} ${
                              item?.person?.standardFamilyName
                            }`}</BodyRegular>
                          </Grid>
                        </Grid>
                      );
                    })}
                  </Grid>
                }
                accordionSummaryContent={
                  <Grid container justifyContent="space-between">
                    <Grid item>
                      <BodyLarge>{`${selectedPlayers?.length} ${t('teams.players')}`}</BodyLarge>
                    </Grid>
                    <Grid item>
                      <BodyLarge>{t('view')}</BodyLarge>
                    </Grid>
                  </Grid>
                }
              />
            </Grid>
          )}
          {isThereTeamsToShow ? (
            <>
              {isThereFilteredTeams && (
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <BodyRegularBold className={classes.radioButtons}>{t('chooseFrom')}</BodyRegularBold>
                  <RadioButtonGroup
                    handleChange={handleOnChangeTeamType}
                    isRow={false}
                    options={[
                      {
                        value: EntryStatusEnum.DIRECT_ACCEPTANCE,
                        label: t('selectedTeams'),
                        disable: !selectedTeams.length,
                      },
                      {
                        value: EntryStatusEnum.ALTERNATE,
                        label: t('alternateTeams'),
                        disable: !alternateTeams.length,
                      },
                    ]}
                    value={choosenTeamType}
                  />
                </Grid>
              )}
              <Grid item sm={12} xs={12}>
                <BodyRegularBold className={classes.teamsLabel}>{t('teams.teamUpper')}</BodyRegularBold>
                <TMXSelect displayEmpty fullWidth value={selectedTeam} onChange={handleChangeTeam}>
                  <MenuItem disabled selected value="">
                    {t('matchTypePh')}
                  </MenuItem>
                  {(isThereFilteredTeams ? teamsToShow : teams).map((team) => {
                    return (
                      <MenuItem key={team?.participantId} value={team?.participantId}>
                        {team?.participantName}
                      </MenuItem>
                    );
                  })}
                </TMXSelect>
                {showChooseTeamError && <FormErrorMessage message={t('moveToTeamError')} />}
              </Grid>
            </>
          ) : (
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <BodyRegular className={classes.noTeamsMsg}>
                <Trans
                  components={{
                    strong: <strong />,
                  }}
                  i18nKey={'moveToTeamNoTeams'}
                />
              </BodyRegular>
              <BodyRegular className={classes.noTeamsLink}>
                <Trans
                  components={{
                    Link: (
                      <Link
                        component={RouterLink}
                        to={getParticipantRoutes(tournamentId, TEAMS_PART)}
                        underline="hover"
                        onClick={onClose}
                      />
                    ),
                  }}
                  i18nKey={'moveToTeamLink'}
                />
              </BodyRegular>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actionsContainer}>
        <StandardButton onClick={onClose}>{isThereFilteredTeams ? t('actions.cancel') : t('Close')}</StandardButton>
        {isThereTeamsToShow && (
          <StandardButton loading={loading || addToTeamInProgress} variant="primary" onClick={handleConfirmAddToTeam}>
            {t('confirm')}
          </StandardButton>
        )}
      </DialogActions>
    </TMXDialog>
  );
};

export default AddToTeamDialog;
